import { OcrQuotaPolicy, OcrQuotaSubjectType, type OcrQuotaSummary } from '../schemas'
import { OcrQuotaActions, type OcrQuotaActionProps } from './OcrQuotaActions'

type OcrQuotaMeterProps = OcrQuotaActionProps & {
  summary: OcrQuotaSummary | null
  isTrial: boolean
  hasQueuedSyncedFiles?: boolean
}

const numberFormatter = new Intl.NumberFormat()
const percentageFormatter = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 })

/** A compact near-limit notice. Full usage and request errors belong on the Usage page. */
export function OcrQuotaMeter({ summary, isTrial, onActivateSubscription, onContactSupport, hasQueuedSyncedFiles = false }: OcrQuotaMeterProps) {
  // With enforcement off nothing waits for quota, so a near-limit warning would mislead. A backend that omits the field still enforces.
  if (!summary || summary.enforcement_enabled === false || summary.policy !== OcrQuotaPolicy.Cumulative || summary.counted_pages / summary.page_limit <= 0.9)
    return null

  const title = `${summary.subject_type === OcrQuotaSubjectType.Organization ? 'Organization ' : ''}OCR ${isTrial ? 'Trial ' : ''}Usage`

  return (
    <section className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <h2 className="font-semibold text-brand-neutral-950">{title}</h2>
        <span className="tabular-nums text-brand-neutral-800">{percentageFormatter.format(summary.counted_pages / summary.page_limit)} used</span>
      </div>
      <div
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-yellow-100"
        role="progressbar"
        aria-label="OCR pages used"
        aria-valuemin={0}
        aria-valuemax={summary.page_limit}
        aria-valuenow={Math.min(summary.counted_pages, summary.page_limit)}
        aria-valuetext={`${numberFormatter.format(summary.counted_pages)} of ${numberFormatter.format(summary.page_limit)} pages`}
      >
        <div className="h-full rounded-full bg-yellow-600" style={{ width: `${Math.min(100, (summary.counted_pages / summary.page_limit) * 100)}%` }} />
      </div>
      {hasQueuedSyncedFiles && (
        <p className="mt-2 text-xs text-brand-neutral-700">
          Files from connected folders share this allowance and resume OCR automatically when enough quota is available.
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-brand-neutral-700">{numberFormatter.format(summary.remaining_pages)} pages remaining</span>
        <OcrQuotaActions onActivateSubscription={onActivateSubscription} onContactSupport={onContactSupport} showUsage={isTrial} />
      </div>
    </section>
  )
}
